import * as React from "react";
import BaseModel from "../../models/BaseModel";
import WatchableValue from "../../models/WatchableValue";
import ColumnFilterUI from "./ColumnFilterUI";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import type { AdditionalColumnConfig, ColumnConfig } from "./UniversalObjectTable";

export type UniversalObjectTableHeaderRowProps = {
  id: string;
  selectedObjects: WatchableValue<Set<BaseModel>> | undefined;
  objects: WatchableValue<BaseModel[]>;
  onSelectAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFilterChange: (columnConfig: ColumnConfig) => void;
  grandparentColumnConfig?: ColumnConfig;
  parentColumnConfig?: ColumnConfig;
  objectColumnConfig: ColumnConfig;

  additionalColumns: AdditionalColumnConfig[] | undefined;
};

export default function UniversalObjectTableHeaderRow(props: UniversalObjectTableHeaderRowProps) {
  const {
    id,
    selectedObjects,
    objects,
    onSelectAll,
    onFilterChange,
    grandparentColumnConfig,
    parentColumnConfig,
    objectColumnConfig,
    additionalColumns,
  } = props;

  const showGrandparent = grandparentColumnConfig != null;
  const showParent = parentColumnConfig != null;

  useOnChangeWatcher(objects);

  function SelectAllCheckbox() {
    const allSelected =
      selectedObjects != null && objects.value.length > 0 && objects.value.every((o) => selectedObjects.value.has(o));

    const someSelected = selectedObjects != null && objects.value.some((o) => selectedObjects.value.has(o));
    const selectAllIndeterminate = someSelected && !allSelected;
    const selectAllInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
      if (selectAllInputRef.current) selectAllInputRef.current.indeterminate = selectAllIndeterminate;
    }, [selectAllIndeterminate]);

    useOnChangeWatcher(selectedObjects, () => {
      return true;
    });

    if (selectedObjects == null) {
      return null;
    }

    return (
      <div
        style={{
          height: "35px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <input
          type="checkbox"
          ref={selectAllInputRef}
          checked={allSelected}
          style={{ height: "15px" }}
          id={`${id}_selectAll`}
          onChange={onSelectAll}
        />
      </div>
    );
  }

  return (
    <div
      id="UniversalObjectTableHeaderRow"
      className="headerRow header2 estadaRow left"
      style={{
        gap: "0px",
        paddingLeft: "0px",
        overflow: "visible",
        minWidth: "100%",
      }}
    >
      <SelectAllCheckbox />

      {additionalColumns?.map((column, index) =>
        column.position == "left" ? (
          <ColumnFilterUI
            className={`acol-${index}`}
            columnConfig={column.columnConfig}
            selectId={`${id}_${index}filter`}
            opts={{ preserveOptions: false }}
            onFilterChange={() => onFilterChange(column.columnConfig)}
          />
        ) : null,
      )}

      {showGrandparent && grandparentColumnConfig && (
        <ColumnFilterUI
          className="col-grandparent"
          columnConfig={grandparentColumnConfig}
          selectId={`${id}_Grandparentselection`}
          onFilterChange={() => onFilterChange(grandparentColumnConfig)}
        />
      )}

      {showParent && parentColumnConfig && (
        <ColumnFilterUI
          className="col-parent"
          columnConfig={parentColumnConfig}
          selectId={`${id}_Parentselection`}
          onFilterChange={() => onFilterChange(parentColumnConfig)}
        />
      )}

      {objectColumnConfig && (
        <ColumnFilterUI
          className="col-object"
          style={{ flexGrow: (additionalColumns?.length || 0) > 0 ? "0" : "1" }}
          columnConfig={objectColumnConfig}
          selectId={`${id}_Objectfilter`}
          onFilterChange={() => onFilterChange(objectColumnConfig)}
        />
      )}

      {additionalColumns?.map((column, index) =>
        column.position == "right" ? (
          <ColumnFilterUI
            className={`acol-${index}`}
            columnConfig={column.columnConfig}
            selectId={`${id}_${index}filter`}
            opts={{ preserveOptions: false }}
            onFilterChange={() => onFilterChange(column.columnConfig)}
          />
        ) : null,
      )}
    </div>
  );
}
